import { createInertiaApp } from '@inertiajs/react'
import { createElement, type ReactNode } from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'

type ResolvedComponent = {
  default: ReactNode
  layout?: (page: ReactNode) => ReactNode
}

createInertiaApp({
  resolve: async (name) => {
    const pages = import.meta.glob<ResolvedComponent>('../pages/**/*.tsx')
    const load = pages[`../pages/${name}.tsx`]
    if (!load) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)
      return undefined as unknown as ResolvedComponent
    }
    return load()
  },

  setup({ el, App, props }) {
    if (el && el.hasChildNodes()) {
      hydrateRoot(el, createElement(App, props))
    } else if (el) {
      createRoot(el).render(createElement(App, props))
    } else {
      console.error(
        'Missing root element. Move `vite_typescript_tag "inertia"` into an Inertia-specific layout.',
      )
    }
  },
})

// iOS standalone PWA recovery: returning from an external link (e.g. opening a
// LinkedIn post) can leave the PWA showing a blank white screen because the SPA
// was torn down and never re-rendered. When we come back visible and the app
// root is genuinely empty, reload to re-render. Gated to standalone + empty-root
// so a healthy page is never reloaded, and to browser-only globals so SSR is
// unaffected (this entrypoint runs on the client only).
function installPwaBlankScreenRecovery(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  const nav = window.navigator as Navigator & { standalone?: boolean }
  const standalone =
    window.matchMedia?.('(display-mode: standalone)')?.matches === true || nav.standalone === true
  if (!standalone) return

  const recoverIfBlank = (): void => {
    if (document.visibilityState !== 'visible') return
    const root = document.getElementById('app')
    if (root && root.childElementCount === 0) window.location.reload()
  }

  window.addEventListener('pageshow', (event) => {
    if ((event as PageTransitionEvent).persisted) recoverIfBlank()
  })
  document.addEventListener('visibilitychange', recoverIfBlank)
}

installPwaBlankScreenRecovery()
